import { arrayOf, func, number, oneOfType, shape, string } from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import __ from "web/Layout/Translations";

import Anchor from "web/Components/Common/Anchor";

import ArrowLeftIcon from "web/assets/icons/arrow-left.svg";
import ArrowRightIcon from "web/assets/icons/arrow-right.svg";

import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";

import { useGetCustomerDetailsQuery } from "web/features/customer/customerApiSlice";
import { useGetEmployeeDetailsQuery } from "web/features/employee/employeeApiSlice";

import classes from "./list.scss";

const getLink = (path) => (!path.indexOf("/") ? path : `/${path}`);

const redirectToCategory = (categoryObj) => {
  switch (true) {
    case categoryObj.url_key === "turystyka-online":
      return getLink("tourism");
    default:
      return getLink(categoryObj.request_path);
  }
};

const getParentCategoryId = (category) => {
  const splitCategory = category.path.split("/");
  return parseInt(splitCategory[splitCategory.length - 2], 10);
};

const NavigationList = ({
  rootCategoryId,
  startCategoryId,
  categoriesTree,
  categoriesData,
  categoriesSizes,
  setCategory,
  closeNavigationAction,
}) => {
  const {
    app: { storeConfig },
  } = useSelector((state) => state);
  const { data: customer } = useGetCustomerDetailsQuery();
  const { language } = customer || {};
  const { data: employee } = useGetEmployeeDetailsQuery();

  const isPL = language === "pl_PL";

  const {
    mybenefit_active_url: myBenefitUrlPl,
    mybenefit_active_url_en: myBenefitUrlEn,
    mybenefit_active_menu: myBenefitActiveMenuPl,
    mybenefit_active_menu_en: myBenefitActiveMenuEn,
    msi_url: msiUrlPl,
    msi_url_en: msiUrlEn,
  } = storeConfig;
  const { functionalities, mbActiveEnabled } = employee;

  const myBenefitUrl = isPL ? myBenefitUrlPl : myBenefitUrlEn;
  const myBenefitActiveMenu = isPL
    ? myBenefitActiveMenuPl
    : myBenefitActiveMenuEn;
  const msiUrl = isPL ? msiUrlPl : msiUrlEn;

  const dispatch = useDispatch();
  const initialStartCategoryId = startCategoryId === 2 ? null : startCategoryId;
  const treeCurrent = categoriesTree.reduce((result, path) => {
    switch (true) {
      case !!initialStartCategoryId: {
        const startCategoryIdStringBegin = `${startCategoryId}/`;
        const startCategoryIdStringMiddle = `/${startCategoryId}/`;

        let pathSliced;
        if (path.indexOf(startCategoryIdStringBegin) === 0) {
          pathSliced = path.slice(startCategoryIdStringBegin.length);
        }

        if (path.indexOf(startCategoryIdStringMiddle) !== -1) {
          pathSliced = path.slice(
            path.indexOf(startCategoryIdStringMiddle) +
              startCategoryIdStringMiddle.length
          );
        }

        if (pathSliced?.length) {
          const idChild = parseInt(pathSliced, 10);
          const categoryDataChild =
            idChild &&
            categoriesData.find(
              (category) => category && +category.id === +idChild
            );
          return categoryDataChild ? [...result, categoryDataChild] : result;
        }

        return result;
      }
      case path.indexOf("/") === -1: {
        const categoryData = categoriesData.find(
          (category) => category && +category.id === +path
        );
        return categoryData ? [...result, categoryData] : result;
      }
      default: {
        return result;
      }
    }
  }, []);
  const treeCurrentUnique = [
    ...new Map(treeCurrent.map((item) => [item.id, item])).values(),
  ];
  const startCategoryData = initialStartCategoryId
    ? categoriesData.find((category) => +category.id === +startCategoryId)
    : null;

  const list = treeCurrentUnique
    .sort((a, b) => a?.position - b?.position)
    .map((category) => {
      const categoryIdStringBegin = `${category.id}/`;
      const categoryIdStringMiddle = `/${category.id}/`;
      const parentCategoryId = getParentCategoryId(category);
      const hasSubcategory = categoriesTree.some((categoryPath) => {
        switch (true) {
          case categoryPath.indexOf(categoryIdStringBegin) === 0:
          case categoryPath.indexOf(categoryIdStringMiddle) !== -1: {
            return true;
          }
          default: {
            return false;
          }
        }
      });

      const content = (
        <>
          <span>{category.name}</span>
          <ArrowRightIcon width={10} />
        </>
      );

      if (categoriesSizes.find((x) => x.id === category.id)) {
        if (hasSubcategory) {
          return (
            <button
              key={category.id}
              type="button"
              className={classes.navItem}
              onClick={() => dispatch(setCategory(category.id))}
            >
              {content}
            </button>
          );
        }
        return (
          <Link
            key={category.id}
            className={classes.navItem}
            to={redirectToCategory(category)}
            onClick={() => {
              dispatch(setCategory(parentCategoryId));
              dispatch(closeNavigationAction());
            }}
          >
            {content}
          </Link>
        );
      }
      return null;
    });

  const additionalList = isArrayHasItems(functionalities)
    ? functionalities
        .filter(
          (functionality) =>
            functionality === "enable_msi_cards" ||
            (functionality === "enable_mybenefit_active" && mbActiveEnabled)
        )
        .map((item) => {
          const isMsiCard = item === "enable_msi_cards";

          const content = (
            <>
              <span>{isMsiCard ? "Multisport" : myBenefitActiveMenu}</span>
              <ArrowRightIcon width={10} />
            </>
          );
          return (
            <Anchor
              key={item}
              className={classes.navItem}
              to={isMsiCard ? msiUrl : myBenefitUrl}
            >
              {content}
            </Anchor>
          );
        })
    : [];

  if (!startCategoryData) {
    list?.push(...additionalList);
  }

  return startCategoryData ? (
    <div className={classes.subcategoryRoot}>
      <div className={classes.subcategory}>
        <button
          className={classes.subcategoryHeader}
          type="button"
          onClick={() => {
            const pathSplit = startCategoryData.path.split("/");
            const currentIdIndex = pathSplit.indexOf(`${startCategoryData.id}`);
            const parentId = currentIdIndex
              ? +pathSplit[currentIdIndex - 1]
              : null;
            const parentIdProcessed =
              parentId && parentId !== rootCategoryId ? parentId : null;
            dispatch(setCategory(parentIdProcessed));
          }}
        >
          <ArrowLeftIcon width={10} />
          <span>{startCategoryData.name}</span>
        </button>
        <Link
          className={classes.navItem}
          to={getLink(startCategoryData.request_path)}
          onClick={() => dispatch(closeNavigationAction())}
        >
          <span>{__("Wszystkie w ") + startCategoryData.name}</span>
          <ArrowRightIcon width={10} />
        </Link>
        {list}
      </div>
    </div>
  ) : (
    <div className={classes.categoryRoot}>{list}</div>
  );
};

NavigationList.propTypes = {
  rootCategoryId: oneOfType([string, number]).isRequired,
  startCategoryId: oneOfType([number, string]),
  setCategory: func.isRequired,
  closeNavigationAction: func.isRequired,
  categoriesTree: arrayOf(string).isRequired,
  categoriesSizes: arrayOf(shape({})).isRequired,
  categoriesData: arrayOf(shape({})).isRequired,
};

NavigationList.defaultProps = {
  startCategoryId: "",
};

export default NavigationList;
